import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, ArrowLeftRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Persistent top-of-viewport banner shown whenever the current session carries an
// `impersonated_by` claim. One-click return to admin restores the original session.
export default function ImpersonationBanner() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!user?.impersonated_by) return null;

  const endImpersonation = async () => {
    setBusy(true);
    try {
      await api.post("/admin/end-impersonation");
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Returned to your admin account.");
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not end impersonation — please sign in again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-[95] bg-[#B35C4A] text-white shadow-md"
      data-testid="impersonation-banner"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2.5 text-sm">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <div>
            <span className="text-[10px] uppercase tracking-widest opacity-80 mr-2">Admin session</span>
            You are viewing as{" "}
            <strong className="font-semibold" data-testid="impersonation-target-email">{user.email}</strong>
            <span className="hidden sm:inline text-white/80"> · {user.role} · Session expires in 2h</span>
          </div>
        </div>
        <button
          type="button"
          onClick={endImpersonation}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold px-3 py-1.5 bg-white text-[#B35C4A] hover:bg-white/90 transition disabled:opacity-50"
          data-testid="end-impersonation-btn"
        >
          <ArrowLeftRight className="w-3.5 h-3.5" />
          {busy ? "Returning…" : "Return to admin"}
        </button>
      </div>
    </div>
  );
}
